import _defineProperty from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _toConsumableArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/toConsumableArray";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { useState, useCallback, useRef, useMemo } from 'react';
import { message } from 'antd';

var isSameFile = function isSameFile(a, b) {
  return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified && a.type === b.type;
};

export var useFileSelector = function useFileSelector() {
  var _useState = useState({
    uploadFiles: [],
    importUrls: [],
    libraryFiles: []
  }),
      state = _useState[0],
      setState = _useState[1];

  var stateRef = useRef(state);
  stateRef.current = state; // Computed values

  var uploadFileCount = state.uploadFiles.length;
  var importUrlCount = state.importUrls.length;
  var libraryFileCount = state.libraryFiles.length;
  var totalFilesCount = uploadFileCount + importUrlCount + libraryFileCount;
  var isLimitReached = useCallback(function (maxFileUpload) {
    return totalFilesCount >= maxFileUpload;
  }, [totalFilesCount]); // Actions

  var addUploadFiles = useCallback(function (files, maxFileUpload) {
    if (isLimitReached(maxFileUpload)) {
      message.warning("You can only add up to ".concat(maxFileUpload, " file(s) in total"));
      return false;
    }

    setState(function (prevState) {
      var newUploadFiles = _toConsumableArray(prevState.uploadFiles);

      var addedCount = 0;
      files.forEach(function (file) {
        var exists = newUploadFiles.some(function (existing) {
          return isSameFile(existing, file);
        });

        if (!exists && totalFilesCount + addedCount < maxFileUpload) {
          newUploadFiles.push(file);
          addedCount++;
        }
      });

      if (addedCount === 0 && files.length > 0) {
        message.warning('No new files were added (either duplicates or limit reached)');
      }

      return _objectSpread(_objectSpread({}, prevState), {}, {
        uploadFiles: newUploadFiles
      });
    });
    return true;
  }, [totalFilesCount]);
  var removeUploadFile = useCallback(function (targetFile) {
    setState(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        uploadFiles: prevState.uploadFiles.filter(function (file) {
          return !isSameFile(file, targetFile);
        })
      });
    });
  }, []);
  var addImportUrl = useCallback(function (url, maxFileUpload) {
    if (isLimitReached(maxFileUpload)) {
      message.warning("You can only add up to ".concat(maxFileUpload, " file(s) in total"));
      return false;
    }

    setState(function (prevState) {
      if (prevState.importUrls.includes(url.trim())) {
        message.warning('This URL has already been added');
        return prevState;
      }

      if (totalFilesCount >= maxFileUpload) {
        message.warning("You can only add up to ".concat(maxFileUpload, " file(s) in total"));
        return prevState;
      }

      return _objectSpread(_objectSpread({}, prevState), {}, {
        importUrls: [].concat(_toConsumableArray(prevState.importUrls), [url.trim()])
      });
    });
    return true;
  }, [totalFilesCount]);
  var removeImportUrl = useCallback(function (targetUrl) {
    setState(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        importUrls: prevState.importUrls.filter(function (url) {
          return url !== targetUrl;
        })
      });
    });
  }, []);
  var addLibraryFile = useCallback(function (file, maxFileUpload) {
    if (isLimitReached(maxFileUpload)) {
      message.warning("You can only add up to ".concat(maxFileUpload, " file(s) in total"));
      return false;
    }

    setState(function (prevState) {
      if (prevState.libraryFiles.some(function (existing) {
        return existing.id === file.id;
      })) {
        message.warning('This file has already been added');
        return prevState;
      }

      if (totalFilesCount >= maxFileUpload) {
        message.warning("You can only add up to ".concat(maxFileUpload, " file(s) in total"));
        return prevState;
      }

      return _objectSpread(_objectSpread({}, prevState), {}, {
        libraryFiles: [].concat(_toConsumableArray(prevState.libraryFiles), [file])
      });
    });
    return true;
  }, [totalFilesCount]);
  var removeLibraryFile = useCallback(function (targetFile) {
    setState(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        libraryFiles: prevState.libraryFiles.filter(function (file) {
          return file.id !== targetFile.id;
        })
      });
    });
  }, []);
  var clearAll = useCallback(function () {
    setState({
      uploadFiles: [],
      importUrls: [],
      libraryFiles: []
    });
  }, []);
  var getFiles = useCallback(function () {
    return stateRef.current.uploadFiles;
  }, []);
  var getUrls = useCallback(function () {
    return stateRef.current.importUrls;
  }, []);
  var getLibraryFiles = useCallback(function () {
    return stateRef.current.libraryFiles;
  }, []);
  return useMemo(function () {
    return {
      // State
      uploadFiles: state.uploadFiles,
      importUrls: state.importUrls,
      libraryFiles: state.libraryFiles,
      // Computed
      totalFilesCount: totalFilesCount,
      isLimitReached: isLimitReached,
      uploadFileCount: uploadFileCount,
      importUrlCount: importUrlCount,
      libraryFileCount: libraryFileCount,
      // Actions
      addUploadFiles: addUploadFiles,
      removeUploadFile: removeUploadFile,
      addImportUrl: addImportUrl,
      removeImportUrl: removeImportUrl,
      addLibraryFile: addLibraryFile,
      removeLibraryFile: removeLibraryFile,
      clearAll: clearAll,
      getFiles: getFiles,
      getUrls: getUrls,
      getLibraryFiles: getLibraryFiles
    };
  }, [state.uploadFiles, state.importUrls, state.libraryFiles, totalFilesCount, isLimitReached, uploadFileCount, importUrlCount, libraryFileCount, addUploadFiles, removeUploadFile, addImportUrl, removeImportUrl, addLibraryFile, removeLibraryFile, clearAll, getFiles, getUrls, getLibraryFiles]);
};